import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Inbox, CheckCheck, Trash2, Calendar, CheckSquare, Clock, MessageSquare, AlertTriangle, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { playNotificationSound } from '@/lib/pushNotifications';
import { updateFavicon } from '@/lib/faviconBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// LocalStorage key used to remember the last unread count we showed the user.
// Persisting across polls + refreshes means we only chime when something is
// actually new, not every time the tab reloads with N unread still sitting.
const LAST_UNREAD_KEY = 'notif_last_unread_count';

const typeIcon = {
  schedule: Calendar,
  task: CheckSquare,
  clock: Clock,
  message: MessageSquare,
  alert: AlertTriangle,
  general: Bell,
};

const typeColor = {
  schedule: 'text-blue-500',
  task: 'text-purple-500',
  clock: 'text-green-500',
  message: 'text-orange-500',
  alert: 'text-red-500',
  general: 'text-gray-500',
};

export function NotificationCenter() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const dropdownRef = useRef(null);
  // Last-seen unread count (seeded from localStorage). Used to detect a JUMP
  // in the count across polls so we can chime + vibrate on NEW notifications.
  // This works for any user (admin / field worker / accounts) regardless of
  // whether they've enabled Web Push — the sound fires on in-app delivery.
  const lastUnreadRef = useRef(
    parseInt(localStorage.getItem(LAST_UNREAD_KEY) || '0', 10) || 0
  );
  const firstFetchRef = useRef(true);

  const fetchUnreadCount = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/notifications/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const nextCount = data.count || 0;
        // If the count went UP (and this isn't the very first load), a new
        // in-app notification landed — chime + vibrate + show a preview
        // toast on every device, regardless of Web Push permission.
        // First-load chime is suppressed so users don't hear a bell for
        // stale unreads on every tab reopen.
        if (!firstFetchRef.current && nextCount > lastUnreadRef.current) {
          try { playNotificationSound(); } catch {}
          if (navigator?.vibrate) {
            try { navigator.vibrate([200, 80, 200]); } catch {}
          }
          // Fetch the freshest notifications so we can show a short preview.
          // We only toast the new ones (those whose count fits in the delta),
          // capped at 3 to avoid flooding the screen.
          const delta = Math.min(nextCount - lastUnreadRef.current, 3);
          try {
            const listRes = await fetch(`${API_URL}/api/notifications`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (listRes.ok) {
              const list = await listRes.json();
              const unreadList = (list || []).filter(n => !n.is_read).slice(0, delta);
              unreadList.forEach((n) => {
                toast(n.title || 'New notification', {
                  description: n.body || '',
                  duration: 6000,
                  action: n.url ? {
                    label: 'Open',
                    onClick: () => { window.location.href = n.url; },
                  } : undefined,
                });
              });
            }
          } catch {}
        }
        firstFetchRef.current = false;
        lastUnreadRef.current = nextCount;
        try { localStorage.setItem(LAST_UNREAD_KEY, String(nextCount)); } catch {}
        setUnreadCount(nextCount);
      }
    } catch (e) {}
  }, []);

  const fetchNotifications = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setNotifications(await res.json());
      }
    } catch (e) {}
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Mirror unreadCount into the browser tab title AND the favicon so users
  // notice new activity even when the tab is backgrounded or pinned. Strips
  // any previous "(N) " prefix so the title doesn't stack.
  useEffect(() => {
    const stripped = document.title.replace(/^\(\d+\)\s+/, '');
    document.title = unreadCount > 0 ? `(${unreadCount}) ${stripped}` : stripped;
    updateFavicon(unreadCount);
  }, [unreadCount]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Close panel on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) &&
          dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const markAllRead = async () => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/api/notifications/mark-read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUnreadCount(0);
      lastUnreadRef.current = 0;
      try { localStorage.setItem(LAST_UNREAD_KEY, '0'); } catch {}
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (e) {}
  };

  const markOneRead = async (id) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/api/notifications/${id}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => {
        const next = Math.max(0, prev - 1);
        lastUnreadRef.current = next;
        try { localStorage.setItem(LAST_UNREAD_KEY, String(next)); } catch {}
        return next;
      });
    } catch (e) {}
  };

  const deleteNotification = async (id) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/api/notifications/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const removed = notifications.find(n => n.id === id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (removed && !removed.is_read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {}
  };

  const timeAgo = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notification-center">
      {/* Bell Button with Badge */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        className="relative"
        data-testid="notification-bell"
      >
        <Inbox className="h-6 w-6 text-gray-600" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full text-xs font-bold text-white"
            style={{ background: '#ef4444', animation: 'pulse 2s infinite' }}
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Notification Panel */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-2xl border z-[100] overflow-hidden max-sm:fixed max-sm:left-2 max-sm:right-2 max-sm:top-14 max-sm:w-auto max-sm:max-w-none"
          style={{ maxHeight: '480px' }}
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
            <h3 className="font-semibold text-sm">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  data-testid="mark-all-read"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
            {loading ? (
              <p className="text-center text-gray-400 text-sm py-8">Loading...</p>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8 px-4">
                <Inbox className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No notifications yet</p>
              </div>
            ) : (
              notifications.map(notif => {
                const IconComp = typeIcon[notif.type] || Bell;
                const iconClass = typeColor[notif.type] || 'text-gray-500';
                return (
                  <div
                    key={notif.id}
                    className={`flex items-start gap-3 px-4 py-3 border-b hover:bg-gray-50 transition-colors cursor-pointer ${!notif.is_read ? 'bg-blue-50/50' : ''}`}
                    onClick={() => { if (!notif.is_read) markOneRead(notif.id); }}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className={`mt-0.5 ${iconClass}`}>
                      <IconComp className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${!notif.is_read ? 'font-semibold' : 'font-normal'} text-gray-800 leading-tight`}>
                          {notif.title}
                        </p>
                        <button onClick={(e) => { e.stopPropagation(); deleteNotification(notif.id); }}
                          className="text-gray-300 hover:text-red-500 flex-shrink-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.body}</p>
                      <p className="text-xs text-gray-400 mt-1">{timeAgo(notif.created_at)}</p>
                    </div>
                    {!notif.is_read && (
                      <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
}

export default NotificationCenter;
